import initTHEOplayerMux from "@mux/mux-data-theoplayer";
import appConfig from "constants/appConfig";
import firebase from "firebase/app";
import mux from "mux-embed";

import type { THEOPlayerRuntime } from "./videojsComponent/theoplayerRuntime";
import type * as THEOplayer from "theoplayer";

/**
 * Mux Data の custom_1 に送る再生経路。
 * dashboard で経路別 (IVS / LL-HLS / 通常HLS) に視聴データを集計するために使う
 */
export type MuxPlaybackMode = "IVS" | "LL-HLS" | "HLS";

/**
 * Mux Data の video_cdn に送る配信CDN。
 * dashboard で CDN 別 (CloudFront / Cloudflare Workers) に視聴データを集計するために使う。
 * IVS は AWS 側の配信網で CDN を選択しないため送らない
 */
export type MuxCdn = "cloudfront" | "cloudflare";

export type MuxDataMonitorForTHEOPlayer = {
  register: (metadata: {
    vid: string;
    isLive: boolean;
    playbackMode: MuxPlaybackMode;
    cdn?: MuxCdn;
  }) => void;
  destroy: () => void;
};

/**
 * IVS 用のモニター。register / destroy は THEOplayer 用と同じ形で、emitError だけ増える。
 *
 * IVS は WASM 層で映像を取得し video 要素へ MSE で流し込むため、再生に失敗しても
 * video 要素は error を発火しない。要素監視だけではエラーを検知できないので、
 * IVS Player のエラーイベントを受け取った呼び出し側が emitError で明示的に転送する。
 */
export type MuxDataElementMonitorForIVS = MuxDataMonitorForTHEOPlayer & {
  emitError: (error: {
    type: string;
    source: string;
    code: number;
    message: string;
  }) => void;
};

/**
 * Mux Data を無効にする場合（featureFlags/muxData が無効）は、
 * createMuxDataMonitorForTHEOPlayer / createMuxDataElementMonitorForIVS を呼ばずにこれを使う
 */
export const noopMuxDataMonitor: MuxDataElementMonitorForIVS = {
  register: () => undefined,
  destroy: () => undefined,
  emitError: () => undefined,
};

/**
 * env key が未設定の環境では何もしない
 */
const getEnabledEnvKey = (): string | undefined => {
  const { envKey } = appConfig.MuxData;
  if (!envKey) {
    return undefined;
  }
  return envKey;
};

const buildVideoMetadata = ({
  vid,
  isLive,
  playbackMode,
  cdn,
}: {
  vid: string;
  isLive: boolean;
  playbackMode: MuxPlaybackMode;
  cdn?: MuxCdn;
}) => {
  return {
    video_id: vid,
    video_title: vid,
    video_stream_type: isLive ? "live" : "on-demand",
    viewer_user_id: firebase.auth()?.currentUser?.uid,
    custom_1: playbackMode,
    video_cdn: cdn,
  };
};

/**
 * Mux Data へ再生メトリクスを送信するモニターを生成する（NPAW/youboraと併用）
 */
export const createMuxDataMonitorForTHEOPlayer = ({
  player,
  theoPlayerLibrary,
}: {
  player: THEOplayer.Player;
  theoPlayerLibrary: THEOPlayerRuntime;
}): MuxDataMonitorForTHEOPlayer => {
  const envKey = getEnabledEnvKey();
  if (envKey === undefined) {
    return noopMuxDataMonitor;
  }

  let monitor: ReturnType<typeof initTHEOplayerMux> | undefined = undefined;
  let currentVid: string | undefined = undefined;
  let currentPlaybackMode: MuxPlaybackMode | undefined = undefined;

  return {
    register({ vid, isLive, playbackMode, cdn }) {
      try {
        const videoMetadata = buildVideoMetadata({
          vid,
          isLive,
          playbackMode,
          cdn,
        });

        if (monitor === undefined) {
          monitor = initTHEOplayerMux(
            player,
            {
              debug: false,
              data: {
                env_key: envKey,
                player_name: "legacy-portal-theoplayer",
                player_init_time: initTHEOplayerMux.utils.now(),
                ...videoMetadata,
              },
            },
            theoPlayerLibrary
          );
          currentVid = vid;
          currentPlaybackMode = playbackMode;
          return;
        }

        // 同一動画の再ロード（画質切替やLL切替）ではビューを分けない
        if (currentVid === vid) {
          // LL切替などで再生経路だけ変わった場合はビューを保ったままメタデータを更新する
          if (currentPlaybackMode !== playbackMode) {
            monitor.updateData({ custom_1: playbackMode });
            currentPlaybackMode = playbackMode;
          }
          return;
        }

        monitor.emit("videochange", videoMetadata);
        currentVid = vid;
        currentPlaybackMode = playbackMode;
      } catch (e) {
        console.error("Failed to register Mux Data monitor:", e);
      }
    },

    destroy() {
      try {
        monitor?.destroy();
      } catch (e) {
        console.error("Failed to destroy Mux Data monitor:", e);
      }
      monitor = undefined;
      currentVid = undefined;
      currentPlaybackMode = undefined;
    },
  };
};

/**
 * IVS プレイヤー用に、再生に使われる video 要素を汎用の HTML5 モニターで監視する
 * （Mux Data に IVS 専用 SDK が無いため）。
 *
 * エラーだけは要素からは観測できないため emitError で受け取る。
 * NPAW は videojs アダプタで一段上のレイヤーを見ており、構図は異なる。
 */
export const createMuxDataElementMonitorForIVS = ({
  getVideoElement,
}: {
  getVideoElement: () => HTMLVideoElement | undefined;
}): MuxDataElementMonitorForIVS => {
  const envKey = getEnabledEnvKey();
  if (envKey === undefined) {
    return noopMuxDataMonitor;
  }

  let monitoredElement: HTMLVideoElement | undefined = undefined;
  let currentVid: string | undefined = undefined;

  return {
    register({ vid, isLive, playbackMode }) {
      try {
        const element = getVideoElement();
        if (element === undefined) {
          console.warn("Mux Data: video element not found, skip monitoring");
          return;
        }

        const videoMetadata = buildVideoMetadata({ vid, isLive, playbackMode });

        if (monitoredElement === undefined) {
          mux.monitor(element, {
            debug: false,
            data: {
              env_key: envKey,
              player_name: "legacy-portal-ivs",
              player_init_time: mux.utils.now(),
              ...videoMetadata,
            },
          });
          monitoredElement = element;
          currentVid = vid;
          return;
        }

        // 同一動画の再ロードではビューを分けない
        if (currentVid === vid) {
          return;
        }

        mux.emit(monitoredElement, "videochange", videoMetadata);
        currentVid = vid;
      } catch (e) {
        console.error("Failed to register Mux Data monitor:", e);
      }
    },

    /**
     * player_error_message はダッシュボードでのグルーピングに使われるため、
     * 個別の事象ではなく種別を渡し、具体的な内容は player_error_context に入れる
     * @see https://www.mux.com/docs/guides/data/monitor-html5-video-element
     */
    emitError({ type, source, code, message }) {
      try {
        if (monitoredElement === undefined) {
          return;
        }
        mux.emit(monitoredElement, "error", {
          player_error_code: code,
          player_error_message: `${type} (${source})`,
          player_error_context: message,
        });
      } catch (e) {
        console.error("Failed to emit Mux Data error:", e);
      }
    },

    destroy() {
      try {
        if (monitoredElement !== undefined) {
          mux.destroyMonitor(monitoredElement);
        }
      } catch (e) {
        console.error("Failed to destroy Mux Data monitor:", e);
      }
      monitoredElement = undefined;
      currentVid = undefined;
    },
  };
};
